// AI chat demo — shared constants.

// Security GUID gating visibility of the AI chat. Provision this in the security
// system and grant it to demo users. For a quick demo you can bypass the gate by
// setting window.config.AI_CHAT_DISABLE_SECURITY_GATE = true in public/config.js.
export const AI_CHAT_SECURITY_GUID = '224BF0F5-ACD7-4A4F-8C8F-E54F1A5C4FBF';

// Security GUID gating the Insights feature ("Beta Test 1"): save/recall/share
// chat results, the Saved Insights page, and all /ai/insights API routes.
export const AI_INSIGHTS_SECURITY_GUID = 'A2F4C8E1-3B5D-47A6-B9C0-D1E2F3A4B5C6';

// Security GUID gating experimental / not-yet-GA Reservoir features. First use:
// the Catalyst chat "Export to email" (.eml) action. Seeded by
// ai/db/migrations/add_experimental_features_security.sql.
export const EXPERIMENTAL_FEATURES_SECURITY_GUID = '066267E1-987F-4679-924E-96E4ADDFE162';

// Security GUID gating access to report builder functionality (propose_report_edit,
// add_visual_component, builder MCP tools). Must match REPORT_BUILDER_SECURITY_GUID in v4.php / ai.php.
export const REPORT_BUILDER_SECURITY_GUID = 'D5A4C9E8-6F7B-4A1C-B2D3-E4F5A6B7C8D9';

// Security GUID gating authoring/approval of freeform "htmljs" report components (the elevated
// permission ABOVE plain REPORT_BUILDER). A holder may add/edit the HTML, libraries and analyze
// prompts of an htmljs node; the server (v4_processHtmlJsComponents) is the authoritative gate and
// re-stamps integrity + approval on save. Must match CUSTOM_COMPONENT_APPROVE_SECURITY_GUID in
// usalco_api/src/lib/security.php. See ai/requests/freeform_report/.
export const CUSTOM_COMPONENT_APPROVE_SECURITY_GUID = '159C5C34-CEC6-44BA-B598-CA074A6B3A62';

// "Reservoir Management" security GUID. Used to gate the report AI setting's "Admin only"
// mode (re_include_in_ai === 2): the chat is shown only to reservoir managers. Matches
// RESERVOIR_MANAGERS_SECURITY_GUID in usalco_api/src/lib/security.php.
export const RESERVOIR_MANAGERS_SECURITY_GUID = '7A9C4F46-7C1F-4C94-9C9E-9F75C72A61E3';

// Values of the report's re_include_in_ai column (the "AI" dropdown on the edit report page).
export const AI_INCLUDE_NO = 0;         // AI chat disabled for the report
export const AI_INCLUDE_YES = 1;        // AI chat enabled for anyone with AI chat access
export const AI_INCLUDE_ADMIN_ONLY = 2; // AI chat enabled only for reservoir managers

// Single source of truth for whether the Catalyst AI chat is enabled for a report view.
// `includeInAI` is the report's re_include_in_ai value (0/1/2; booleans from non-report
// callers are coerced: true→Yes, false→No). `securities` is userContext.securities.
// "Admin only" is strictly narrower than "Yes": it still requires the AI chat security and
// additionally requires the reservoir-management security.
export function computeAiEnabled(includeInAI, securities) {
  const value = Number(includeInAI);
  if (!value) return false;                          // 0 / No / null / undefined → off
  if (!window.config?.CATALYST_URL) return false;
  if (window.config?.AI_CHAT_DISABLE_SECURITY_GATE) return true;
  if (!Array.isArray(securities)) return false;
  const has = (guid) => securities.some((s) => s.ua_fid === guid);
  if (!has(AI_CHAT_SECURITY_GUID)) return false;
  if (value === AI_INCLUDE_ADMIN_ONLY) return has(RESERVOIR_MANAGERS_SECURITY_GUID);
  return true;
}

// Catalyst profile the chat uses. Configured via window.config.CATALYST_REPORTING_PROFILE_ID
// (set in public/config.js); falls back to the legacy seed slug if not set.
export const CATALYST_PROFILE_ID = window.config?.CATALYST_REPORTING_PROFILE_ID ?? 'reservoir-demo';

// Profile used when chatting from inside the v4 report builder. Configured via
// window.config.CATALYST_BUILDER_PROFILE_ID (set in public/config.js).
export const CATALYST_BUILDER_PROFILE_ID = window.config?.CATALYST_BUILDER_PROFILE_ID ?? 'reservoir-builder';

// context.kind published by the report builder. The chat routes to the builder profile and
// ToolResultRenderer swaps the propose_report_edit "Review in builder" button for an in-place Apply.
export const CHAT_KIND_BUILDER = 'report4-builder';

// Prefix on persisted chat titles so Reservoir-originated chats are distinguishable
// from standalone Catalyst chats in the shared (per-OID) history.
export const RESERVOIR_CHAT_PREFIX = '[Reservoir] ';

// Display-tool names whose tool-result payloads the chat renders inline.
export const TOOL_SHOW_CHART = 'show_chart';
export const TOOL_SHOW_TABLE = 'show_table';
export const TOOL_PROPOSE_REPORT_EDIT = 'propose_report_edit';

// Syncfusion visualization display tools (rendered by src/ai/render/*).
export const TOOL_SHOW_GAUGE = 'show_gauge';
export const TOOL_SHOW_TREEMAP = 'show_treemap';
export const TOOL_SHOW_HEATMAP = 'show_heatmap';
export const TOOL_SHOW_SPARKLINE = 'show_sparkline';
export const TOOL_SHOW_MAP = 'show_map';

// Add a single live-bound visual component to a report page — narrower than propose_report_edit.
// Only available in the builder context (reservoir-builder profile).
export const TOOL_ADD_VISUAL_COMPONENT = 'add_visual_component';

// Server-side .pptx builder (catalyst/backend/src/tools/pptx-tools.ts). Its result is a
// base64 file the chat renders as a download card. Enabled on the reservoir-demo profile.
export const TOOL_GENERATE_PRESENTATION = 'generate_presentation';

// Catalyst artifact tools (catalyst/backend/src/tools/artifact-tools.ts): freeform HTML/JS/SVG/
// markdown/code mockups the user iterates on in chat — the authoring phase for freeform report
// components. Executable kinds render in a sandboxed, opaque-origin iframe (src/ai/ArtifactFrame.jsx)
// with the SAME locked-down CSP as the promoted htmljs component. The tools are offered only to users
// with the Experimental Features security (enforced server-side in Catalyst). Result/args shape:
// { artifactId?, version?, kind:'html'|'svg'|'mermaid'|'markdown'|'code', title, content, language?, summary? }.
export const TOOL_CREATE_ARTIFACT = 'create_artifact';
export const TOOL_UPDATE_ARTIFACT = 'update_artifact';
export const ARTIFACT_TOOL_NAMES = [TOOL_CREATE_ARTIFACT, TOOL_UPDATE_ARTIFACT];

export function isArtifactToolPart(part) {
  return !!part && part.type === 'tool' && ARTIFACT_TOOL_NAMES.includes(part.toolName);
}

// Saved Insights (Catalyst chat artifacts). A "result" insight captures one rendered
// display-tool spec ({ toolName, result }); a "conversation" insight captures the whole
// message array. See usalco_api/src/routes/ai_insights.php.
export const AI_INSIGHT_KIND_RESULT = 'result';
export const AI_INSIGHT_KIND_CONVERSATION = 'conversation';

// Tool parts a user can save as a single-result insight: the self-describing display
// tools (NOT propose_report_edit or raw MCP activity). Each renders from part.result
// via ToolResultRenderer, so a stored spec replays identically.
export const SAVABLE_TOOL_NAMES = [
  TOOL_SHOW_CHART, TOOL_SHOW_TABLE, TOOL_SHOW_GAUGE, TOOL_SHOW_TREEMAP,
  TOOL_SHOW_HEATMAP, TOOL_SHOW_SPARKLINE, TOOL_SHOW_MAP,
];

export function isSavableToolPart(part) {
  return !!part && part.type === 'tool' && part.state === 'result'
    && SAVABLE_TOOL_NAMES.includes(part.toolName);
}
